import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stack,
  Paper,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
} from "@mui/material";
import InventoryIcon from "@mui/icons-material/Inventory";
import EngineeringIcon from "@mui/icons-material/Engineering";
import CloseIcon from "@mui/icons-material/Close";
import ScienceIcon from "@mui/icons-material/Science";
import HandymanIcon from "@mui/icons-material/Handyman";
import BugReportIcon from "@mui/icons-material/BugReport";
import SecurityIcon from "@mui/icons-material/Security";
import DescriptionIcon from "@mui/icons-material/Description";
import inventoryService from "../../../api/inventoryService";
import { pluralizeUnit } from "../../Inventario/utils";

const TYPE_ICONS = {
  chemical: <ScienceIcon fontSize="small" />,
  tool: <HandymanIcon fontSize="small" />,
  trap: <BugReportIcon fontSize="small" />,
  epp: <SecurityIcon fontSize="small" />,
  service_format: <DescriptionIcon fontSize="small" />,
};

/**
 * Modal para ver el inventario de un técnico (solo informativo)
 */
const TechnicianInventoryModal = ({ open, onClose, technician }) => {
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      loadInventory();
    } else {
      setInventory([]);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, technician?._id]);

  const loadInventory = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await inventoryService.getInventory();
      
      // El endpoint devuelve un array de registros de stock sin transformar
      // Necesitamos transformarlos al mismo formato que useInventory
      // axios envuelve la respuesta en response.data
      const stockRecords = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      
      // Transformar los datos al formato esperado
      const itemsMap = new Map();
      
      stockRecords.forEach((record) => {
        const item = record.itemId;
        const itemId = item?._id || item || null;
        const location = record.locationId;
        
        if (!itemId || !item || !location) return;
        
        const itemIdStr = itemId.toString();
        
        // Si no existe el item en el mapa, crearlo
        if (!itemsMap.has(itemIdStr)) {
          itemsMap.set(itemIdStr, {
            _id: item._id || itemId,
            name: item.name || "",
            sku: item.sku || "",
            type: item.type || "",
            category: item.category || "",
            brand: item.brand,
            description: item.description,
            unit: item.unit || "",
            cofeprisCat: item.cofeprisCat,
            status: item.status || "active",
            minStock: record.minStock || item.minStock || 0,
            warehouseStock: 0,
            technicians: [],
            hazardousStock: 0,
          });
        }
        
        const product = itemsMap.get(itemIdStr);
        const quantity = record.quantity || 0;
        
        // Solo procesar stock de técnicos
        if (location.type === "technician") {
          const techId = location.technicianId?._id || location.technicianId;
          const techIdStr = techId?.toString() || techId;
          
          const existingTech = product.technicians.find(
            (t) => String(t.technicianId || "") === String(techIdStr || "")
          );
          
          if (existingTech) {
            existingTech.stock += quantity;
          } else {
            product.technicians.push({
              technicianId: techIdStr,
              technicianName: location.technicianName || "",
              stock: quantity,
            });
          }
        }
      });
      
      const allItems = Array.from(itemsMap.values());
      
      // Normalizar el ID del técnico a string para comparación
      // Manejar diferentes formatos de ID (ObjectId, string, etc.)
      let technicianIdStr = null;
      if (technician?._id) {
        const techId = technician._id;
        // Si es un objeto con toString, usarlo; si no, convertir a string
        technicianIdStr = techId?.toString ? techId.toString() : String(techId);
      }
      
      if (technicianIdStr) {
        const technicianInventory = allItems
          .map((item) => {
            const techStock = item.technicians?.find((t) => {
              const techId = t.technicianId;
              const techIdStr = techId?.toString ? techId.toString() : String(techId || "");
              const normalizedTechIdStr = technicianIdStr?.toString ? technicianIdStr.toString() : String(technicianIdStr || "");
              return techIdStr === normalizedTechIdStr;
            });
            
            if (!techStock || (techStock.stock || 0) <= 0) return null;

            return {
              ...item,
              technicianStock: techStock.stock || 0,
            };
          })
          .filter(Boolean);

        setInventory(technicianInventory);
      } else {
        // Resumen total: suma de todos los inventarios de técnicos
        // Primero, obtener un Set de técnicos únicos con stock de todos los items
        const uniqueTechnicians = new Set();
        
        const totalInventory = allItems
          .map((item) => {
            const techniciansWithStock = item.technicians?.filter(t => (t.stock || 0) > 0) || [];
            const totalTechStock = techniciansWithStock.reduce(
              (sum, tech) => sum + (tech.stock || 0),
              0
            );

            if (totalTechStock <= 0) return null;

            // Agregar técnicos únicos al Set
            techniciansWithStock.forEach(tech => {
              const techId = String(tech.technicianId || "");
              if (techId) {
                uniqueTechnicians.add(techId);
              }
            });

            return {
              ...item,
              technicianStock: totalTechStock,
              techniciansCount: techniciansWithStock.length,
            };
          })
          .filter(Boolean);

        // Guardar el conteo de técnicos únicos global en cada item para acceso fácil
        const uniqueCount = uniqueTechnicians.size;
        setInventory(totalInventory.map(item => ({
          ...item,
          uniqueTechniciansCount: uniqueCount
        })));
      }
    } catch (err) {
      console.error("Error cargando inventario:", err);
      setError("No se pudo cargar el inventario");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 12px 48px rgba(0,0,0,0.15)",
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: "primary.main",
          color: "white",
          pb: 2,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2} justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar
              sx={{
                bgcolor: "rgba(255,255,255,0.2)",
                width: 48,
                height: 48,
              }}
            >
              <InventoryIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                {technician ? "Inventario Personal" : "Resumen Total de Inventarios"}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {technician 
                  ? `${technician.name} - Inventario asignado` 
                  : "Suma total de todos los inventarios de técnicos"}
              </Typography>
            </Box>
          </Stack>
          <Button
            onClick={onClose}
            sx={{
              minWidth: "auto",
              color: "white",
              "&:hover": {
                bgcolor: "rgba(255,255,255,0.1)",
              },
            }}
          >
            <CloseIcon />
          </Button>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {error}
          </Alert>
        ) : inventory.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 6,
              px: 3,
            }}
          >
            <InventoryIcon sx={{ fontSize: 80, color: "grey.300", mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Sin inventario
            </Typography>
            <Typography variant="body2" color="text.disabled">
              {technician 
                ? `${technician.name} no tiene artículos asignados en su inventario personal`
                : "No hay artículos asignados a ningún técnico"}
            </Typography>
          </Box>
        ) : (
          <Stack spacing={2}>
            <Box
              sx={{
                bgcolor: "grey.50",
                borderRadius: 2,
                p: 2,
                mb: 1,
              }}
            >
              <Typography variant="body2" color="text.secondary" fontWeight={600} gutterBottom>
                {technician 
                  ? `Artículos en inventario: ${inventory.length}`
                  : `Total de artículos: ${inventory.length}`}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {technician
                  ? `Stock total: ${inventory.reduce((sum, item) => sum + (item.technicianStock || 0), 0)} unidades`
                  : `Stock total en todos los técnicos: ${inventory.reduce((sum, item) => sum + (item.technicianStock || 0), 0)} unidades`}
              </Typography>
              {!technician && inventory.length > 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Distribuido entre {inventory[0]?.uniqueTechniciansCount || 0} técnico{inventory[0]?.uniqueTechniciansCount !== 1 ? 's' : ''}
                </Typography>
              )}
            </Box>

            <List disablePadding>
              {inventory.map((item, index) => (
                <React.Fragment key={item._id}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      mb: 1.5,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                      "&:hover": {
                        bgcolor: "grey.50",
                        borderColor: "primary.main",
                      },
                    }}
                  >
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Avatar
                        sx={{
                          bgcolor: "primary.main",
                          color: "white",
                          width: 48,
                          height: 48,
                        }}
                      >
                        {TYPE_ICONS[item.type] || <InventoryIcon />}
                      </Avatar>

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                          <Typography variant="subtitle1" fontWeight={600} noWrap>
                            {item.name}
                          </Typography>
                          {item.sku && (
                            <Chip
                              label={item.sku}
                              size="small"
                              sx={{ height: 22, fontSize: "0.75rem" }}
                            />
                          )}
                        </Stack>

                        <Stack direction="row" spacing={1} flexWrap="wrap" gap={0.5}>
                          {item.category && (
                            <Chip
                              label={item.category}
                              size="small"
                              variant="outlined"
                              sx={{ height: 22, fontSize: "0.75rem" }}
                            />
                          )}
                        {item.brand && (
                          <Chip
                            label={item.brand}
                            size="small"
                            variant="outlined"
                            sx={{ height: 22, fontSize: "0.75rem" }}
                          />
                        )}
                        {!technician && item.techniciansCount > 0 && (
                          <Chip
                            label={`${item.techniciansCount} técnico${item.techniciansCount > 1 ? 's' : ''}`}
                            size="small"
                            color="info"
                            sx={{ height: 22, fontSize: "0.75rem" }}
                          />
                        )}
                      </Stack>
                    </Box>

                    <Box sx={{ textAlign: "right", minWidth: 100 }}>
                        <Typography variant="h6" fontWeight={700} color="primary.main">
                          {item.technicianStock || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {pluralizeUnit(item.unit, item.technicianStock || 0)}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                  {index < inventory.length - 1 && <Divider sx={{ my: 1 }} />}
                </React.Fragment>
              ))}
            </List>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} variant="contained" size="large" fullWidth>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TechnicianInventoryModal;

